import React, { useState, useEffect, useMemo } from "react";
import { StageChip, ModuleShell, deriveCaseStage, type CaseStage } from "@auction-eth/ui-kit";
import type { Case, FastSnapshot, AuctionInfo, InstitutionMemoEntry } from "@auction-eth/shared-types";
import { api } from "../services/api.js";
import { getCurrentUserId } from "../services/auth.js";
import {
  AuctionApprovalForm, ApprovedBiddingForm,
  WinnerApprovalForm, SettlementForm,
  normalizeCatalogingImageUrls,
} from "./InstitutionModuleForms.js";
import { CaseHistoryTab } from "./CaseHistoryTab.js";
import { CaseComplaintsTab } from "./CaseComplaintsTab.js";
import { AuctionInfoEditor } from "../components/AuctionInfoEditor.js";
import { ImagePreviewModal } from "../components/ImagePreviewModal.js";
import { publicMediaSrc } from "../utils/publicMediaUrl.js";

// ─── Step metadata helpers ────────────────────────────────────────────────────
interface StepInfo { id: string; label: string; owner: string; eta: string; }

const MODULE_META: Record<string, { label: string; owner: string; eta: string }> = {
  initiation:        { label: "Case Initiation",      owner: "Institution",     eta: "Completed" },
  dispatch:          { label: "Field Dispatch",        owner: "Field Seller DAA",eta: "1–4 hrs" },
  field_verification:{ label: "Field Verification",   owner: "Field Seller DAA",eta: "1–2 days" },
  hub_dispatch:      { label: "Hub Dispatch",          owner: "Hub Ops",         eta: "2–6 hrs" },
  hub_witness:       { label: "Hub Witness",           owner: "Hub Ops",         eta: "Same day" },
  hub_processing:    { label: "Hub Processing",        owner: "Hub Ops",         eta: "1–3 days" },
  expert_dispatch:   { label: "Expert Dispatch",       owner: "Field Expert DAA",eta: "2–6 hrs" },
  valuation:         { label: "Expert Valuation",      owner: "Field Expert DAA",eta: "1–2 days" },
  cataloging:        { label: "Cataloging",            owner: "Field Seller DAA",eta: "1 day" },
  auction_approval:  { label: "Auction Approval",      owner: "Institution",     eta: "Your action required" },
  listing:           { label: "Market Listing",        owner: "Marketplace",     eta: "Automated" },
  hub_inspection:    { label: "Hub Inspection",        owner: "Hub Ops",         eta: "1–2 days" },
  approved_bidding:  { label: "Bidding Approval",      owner: "Institution",     eta: "Your action required" },
  bidding:           { label: "Live Bidding",          owner: "Marketplace",     eta: "Per auction schedule" },
  auction_closed:    { label: "Auction Closed",        owner: "Marketplace",     eta: "Automated" },
  winner_approval:   { label: "Winner Approval",       owner: "Institution",     eta: "Your action required" },
  winner_posted:     { label: "Winner Posted",         owner: "Marketplace",     eta: "Automated" },
  settlement:        { label: "Settlement",            owner: "Institution",     eta: "Your action required" },
};

const APP_OWNER: Record<string, string> = {
  institution:   "Institution",
  field_seller:  "Field Seller DAA",
  field_expert:  "Field Expert DAA",
  hub_ops:       "Hub Ops",
  marketplace:   "Marketplace",
};

function buildStepInfoFromSequence(
  sequence: Record<string, { module?: string; application?: string }>
): StepInfo[] {
  const sorted = Object.entries(sequence).sort(([a], [b]) => {
    const num = (s: string) => { const m = s.match(/_(\d+)_/); return m ? parseInt(m[1], 10) : 99; };
    return num(a) - num(b);
  });
  return sorted.map(([id, def]) => {
    const module = def.module ?? "";
    const app = def.application ?? "";
    const meta = MODULE_META[module];
    return {
      id,
      label: meta?.label ?? id.replace(/_/g, " ").replace(/^step \d+ /, ""),
      owner: meta?.owner ?? APP_OWNER[app] ?? app,
      eta: meta?.eta ?? "—",
    };
  });
}

// ─── Shared mini-components ──────────────────────────────────────────────────
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)", display: "block", marginBottom: 4 };
const fieldStyle: React.CSSProperties = { width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--bg)", color: "var(--ink)", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

function DR({ label, value }: { label: string; value: string | number | null | undefined }): React.ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 500 }}>{value != null && value !== "" ? String(value) : "—"}</span>
    </div>
  );
}

/** DAA cataloging output may live under step_9_cataloging or other template keys containing "cataloging". */
function findCatalogPayload(modulePayloads: unknown): Record<string, unknown> | undefined {
  if (!modulePayloads || typeof modulePayloads !== "object") return undefined;
  const mp = modulePayloads as Record<string, unknown>;
  const direct = mp["step_9_cataloging"];
  if (direct && typeof direct === "object") return direct as Record<string, unknown>;
  for (const [stepId, raw] of Object.entries(mp)) {
    if (!raw || typeof raw !== "object") continue;
    const p = raw as Record<string, unknown>;
    if (
      stepId.includes("cataloging") ||
      Boolean(p["cataloged_at"]) ||
      (Boolean(p["asset_name"]) && Array.isArray(p["image_urls"]))
    ) {
      return p;
    }
  }
  return undefined;
}

function collectCatalogImageUrls(modulePayloads: unknown): string[] {
  if (!modulePayloads || typeof modulePayloads !== "object") return [];
  const mp = modulePayloads as Record<string, unknown>;
  const merged: string[] = [];
  for (const [stepId, raw] of Object.entries(mp)) {
    if (!raw || typeof raw !== "object") continue;
    const p = raw as Record<string, unknown>;
    const isCatalog =
      stepId.includes("cataloging") ||
      Boolean(p["cataloged_at"]) ||
      (Boolean(p["asset_name"]) && Array.isArray(p["image_urls"]));
    if (!isCatalog) continue;
    const arr = p["image_urls"];
    if (Array.isArray(arr)) merged.push(...(arr as string[]));
  }
  return normalizeCatalogingImageUrls(merged);
}

function isCatalogModuleRow(stepId: string, p: Record<string, unknown>): boolean {
  return (
    stepId.includes("cataloging") ||
    Boolean(p["cataloged_at"]) ||
    (Boolean(p["asset_name"]) && Array.isArray(p["image_urls"]))
  );
}

function nonCatalogFieldRowVisible(stepId: string, p: Record<string, unknown>): boolean {
  if (isCatalogModuleRow(stepId, p)) return false;
  const extraImgUrls = normalizeCatalogingImageUrls(
    Array.isArray(p["image_urls"]) ? (p["image_urls"] as string[]) : []
  );
  const hasPhotoList = Array.isArray(p["photos"]) && (p["photos"] as unknown[]).length > 0;
  return Boolean(
    p["physical_condition"] ||
      p["valuation_estimate"] ||
      hasPhotoList ||
      extraImgUrls.length > 0 ||
      p["condition_grade"] ||
      p["market_value"]
  );
}

function FieldPhotoGrid({ urls }: { urls: string[] }): React.ReactElement | null {
  const [preview, setPreview] = useState<string | null>(null);
  if (urls.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <ImagePreviewModal url={preview} onClose={() => setPreview(null)} />
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--muted)",
          margin: "0 0 8px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        Photos ({urls.length})
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))",
          gap: 10,
        }}
      >
        {urls.map((src, i) => {
          const displaySrc = publicMediaSrc(src);
          return (
            <button
              key={`${src.slice(0, 80)}-${i}`}
              type="button"
              onClick={() => setPreview(displaySrc)}
              style={{
                display: "block",
                borderRadius: "var(--radius-sm)",
                overflow: "hidden",
                border: "1px solid var(--border)",
                aspectRatio: "1",
                padding: 0,
                cursor: "zoom-in",
                background: "var(--panel)",
              }}
              aria-label={`Preview field photo ${i + 1} larger`}
            >
              <img
                src={displaySrc}
                alt={`Field asset photo ${i + 1}`}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

type Tab = "loan" | "borrower" | "asset" | "auction" | "memo" | "history" | "complaints";

type PlatformBidRow = {
  bid_id?: number;
  user_id?: number;
  username?: string;
  fullname?: string;
  amount?: number;
  status?: string;
  submitted_at?: string;
};

function parsePlatformAuctionResult(pd: unknown): {
  winningBidAmount: number | null;
  winnerFullname: string | null;
  winnerUsername: string | null;
  bidRows: PlatformBidRow[];
} {
  if (!pd || typeof pd !== "object") {
    return { winningBidAmount: null, winnerFullname: null, winnerUsername: null, bidRows: [] };
  }
  const o = pd as Record<string, unknown>;
  const wa = o.winning_amount;
  const fb = o.final_bid;
  let winningBidAmount: number | null = null;
  if (typeof wa === "number" && Number.isFinite(wa)) winningBidAmount = wa;
  else if (typeof fb === "number" && Number.isFinite(fb)) winningBidAmount = fb;
  const winnerFullname = typeof o.winner_fullname === "string" ? o.winner_fullname : null;
  const winnerUsername = typeof o.winner_username === "string" ? o.winner_username : null;
  const bidList = o.bid_list;
  const bidRows: PlatformBidRow[] = Array.isArray(bidList)
    ? bidList.map((raw) => {
      const r = raw as Record<string, unknown>;
      return {
        bid_id: typeof r.bid_id === "number" ? r.bid_id : undefined,
        user_id: typeof r.user_id === "number" ? r.user_id : undefined,
        username: typeof r.username === "string" ? r.username : undefined,
        fullname: typeof r.fullname === "string" ? r.fullname : undefined,
        amount: typeof r.amount === "number" ? r.amount : undefined,
        status: typeof r.status === "string" ? r.status : undefined,
        submitted_at: typeof r.submitted_at === "string" ? r.submitted_at : undefined,
      };
    })
    : [];
  return { winningBidAmount, winnerFullname, winnerUsername, bidRows };
}

function bidRowsHighestFirst(rows: PlatformBidRow[]): PlatformBidRow[] {
  return [...rows].sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export function CaseDetailPage({ caseId, onBack }: { caseId: string; onBack: () => void }): React.ReactElement {
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [stepSequence, setStepSequence] = useState<StepInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("loan");
  const [memoText, setMemoText] = useState("");
  const [savingMemo, setSavingMemo] = useState(false);
  const [editingAuction, setEditingAuction] = useState(false);

  function reload(): void {
    setLoading(true);
    void api.cases.get(caseId)
      .then(async (r) => {
        setCaseData(r.data);
        const tplRef = (r.data as unknown as { template_ref?: { id?: string } }).template_ref;
        if (tplRef?.id) {
          try {
            const tplRes = await api.templates.get(tplRef.id);
            const seq = tplRes.data.sequence ?? {};
            if (Object.keys(seq).length > 0) {
              setStepSequence(buildStepInfoFromSequence(seq));
            }
          } catch { /* template fetch failed — keep default */ }
        }
        setLoading(false);
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "Failed"); setLoading(false); });
  }

  // Silent background refresh — no spinner, just swaps state when data arrives.
  function silentRefresh(): void {
    void api.cases.get(caseId).then((r) => { setCaseData(r.data); }).catch(() => { /* ignore poll errors */ });
  }

  useEffect(() => {
    reload();
    const interval = setInterval(silentRefresh, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const platformAuctionParsed = useMemo(() => {
    const pd = caseData
      ? (caseData as unknown as { platform_data?: Record<string, unknown> }).platform_data
      : undefined;
    return parsePlatformAuctionResult(pd);
  }, [caseData]);

  const bidRowsSorted = useMemo(
    () => bidRowsHighestFirst(platformAuctionParsed.bidRows),
    [platformAuctionParsed.bidRows],
  );

  async function submitMemo(): Promise<void> {
    if (!memoText.trim()) return;
    setSavingMemo(true);
    try {
      await api.cases.appendMemo(caseId, memoText.trim(), getCurrentUserId());
      setMemoText("");
      reload();
    } finally { setSavingMemo(false); }
  }

  if (loading) return <div style={{ textAlign: "center", padding: "80px 20px", color: "var(--muted)" }}>Loading case…</div>;
  if (error || !caseData) return <div role="alert" style={{ background: "var(--danger-bg)", color: "var(--danger)", borderRadius: "var(--radius-sm)", padding: "16px 20px" }}>{error ?? "Case not found"}</div>;

  const c = caseData;
  const allFlags = c.flags as unknown as Record<string, Record<string, boolean | null>>;
  const inst = allFlags["institution"] ?? {};
  const stage = deriveCaseStage({ institution: allFlags["institution"], marketplace: allFlags["marketplace"], hub_ops: allFlags["hub_ops"], is_closed: c.is_closed });

  const mktFlags = allFlags["marketplace"] ?? {};
  const fast = (c as unknown as { fast_snapshot?: FastSnapshot }).fast_snapshot;
  const auctionInfo = (c as unknown as { auction_info?: AuctionInfo }).auction_info;
  // Lock auction_info editing once the auction has been published to the platform (listing_completed).
  const auctionPostedToLegacy = mktFlags["listing_completed"] === true;
  const catalogPayload = findCatalogPayload(c.module_payloads);
  const catalogingPhotoUrls = collectCatalogImageUrls(c.module_payloads);
  const { winningBidAmount, winnerFullname, winnerUsername } = platformAuctionParsed;
  const memos = ((c as unknown as { institution_memo?: InstitutionMemoEntry[] }).institution_memo) ?? [];

  // Next step — derived from the live template sequence
  const currentIdx = stepSequence.findIndex((s) => s.id === c.current_step_id);
  const nextStep = currentIdx >= 0 && currentIdx + 1 < stepSequence.length ? stepSequence[currentIdx + 1] : null;
  const currentStep = currentIdx >= 0 ? stepSequence[currentIdx] : null;

  const stepHistory = ((c as unknown as { step_history?: unknown[] }).step_history) ?? [];

  const TABS: Array<{ id: Tab; label: string }> = [
    { id: "loan", label: "Loan" },
    { id: "borrower", label: "Borrower" },
    { id: "asset", label: "Asset" },
    { id: "auction", label: "Auction Info" },
    { id: "memo", label: `Memo (${memos.length})` },
    { id: "history", label: `History (${stepHistory.length})` },
    { id: "complaints", label: "Complaints" },
  ];

  return (
    <>
      {/* Back */}
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--brand)", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 20 }}>← Back to Queue</button>

      {/* Case header */}
      <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderLeft: `6px solid var(--stage-${stage.replace(/_/g, "-")})`, borderRadius: "var(--radius)", padding: "20px 24px", marginBottom: 20, boxShadow: "var(--shadow)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>{c.case_id}</p>
          <h1 style={{ margin: "6px 0 4px", fontSize: 20, fontWeight: 900, color: "var(--ink)" }}>{fast?.borrower_info?.full_name ?? c.borrower_id ?? "Unknown Borrower"}</h1>
          <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>Loan: {c.linked_loan_id} · Lender: {c.lender_id}</p>
        </div>
        <StageChip stage={stage as CaseStage} />
      </div>

      {/* Workflow progress timeline */}
      <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "16px 20px", marginBottom: 20, boxShadow: "var(--shadow)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>Workflow Progress</p>
          {currentIdx >= 0 && (
            <p style={{ margin: 0, fontSize: 11, color: "var(--muted)" }}>Step {currentIdx + 1} of {stepSequence.length}</p>
          )}
        </div>
        <div style={{ overflowX: "auto", paddingBottom: 4 }}>
          <div style={{ display: "flex", gap: 0, minWidth: "max-content" }}>
            {stepSequence.map((s, idx) => {
              const isDone = currentIdx >= 0 && idx < currentIdx;
              const isActive = idx === currentIdx;
              const isFuture = currentIdx < 0 || idx > currentIdx;
              const isInstitution = s.owner === "Institution";
              return (
                <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 72 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800,
                      background: isDone ? "var(--good, #22c55e)" : isActive ? (isInstitution ? "#3b82f6" : "#f59e0b") : "var(--border)",
                      color: isDone || isActive ? "#fff" : "var(--muted)",
                      border: isActive ? `2px solid ${isInstitution ? "#93c5fd" : "#fcd34d"}` : "2px solid transparent",
                      boxShadow: isActive ? `0 0 0 3px ${isInstitution ? "rgba(59,130,246,0.2)" : "rgba(245,158,11,0.2)"}` : "none",
                    }}>
                      {isDone ? "✓" : idx + 1}
                    </div>
                    <p style={{
                      margin: "4px 0 0", fontSize: 9, fontWeight: isActive ? 800 : 600, textAlign: "center", lineHeight: 1.3,
                      color: isDone ? "var(--good, #22c55e)" : isActive ? (isInstitution ? "#3b82f6" : "#f59e0b") : "var(--muted)",
                      maxWidth: 68, wordBreak: "break-word",
                    }}>{s.label}</p>
                  </div>
                  {idx < stepSequence.length - 1 && (
                    <div style={{ width: 16, height: 2, background: isDone ? "var(--good, #22c55e)" : "var(--border)", flexShrink: 0, marginBottom: 18 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {currentStep && !c.is_closed && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: currentStep.owner === "Institution" ? "rgba(59,130,246,0.08)" : "rgba(245,158,11,0.08)", borderRadius: "var(--radius-sm)", display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 14 }}>{currentStep.owner === "Institution" ? "⚡" : "⏳"}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>
              {currentStep.label}
              <span style={{ fontWeight: 400, color: "var(--muted)", marginLeft: 6 }}>— {currentStep.owner} · ~{currentStep.eta}</span>
            </span>
            {nextStep && (
              <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: "auto" }}>
                Next: {nextStep.label}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 5-category tabs */}
      <div style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 24, boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)", overflowX: "auto" }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ padding: "12px 20px", border: "none", background: activeTab === t.id ? "var(--brand)" : "transparent", color: activeTab === t.id ? "#fff" : "var(--muted)", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", borderBottom: activeTab === t.id ? "2px solid var(--brand)" : "2px solid transparent" }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ padding: "20px 24px" }}>

          {/* Loan */}
          {activeTab === "loan" && (
            fast?.loan_detail ? <>
              <DR label="Loan Reference" value={fast.loan_detail.loan_ref} />
              <DR label="Amount" value={`${fast.loan_detail.currency} ${fast.loan_detail.amount?.toLocaleString()}`} />
              <DR label="Interest Rate" value={fast.loan_detail.interest_rate ? `${fast.loan_detail.interest_rate}%` : null} />
              <DR label="Disbursed At" value={fast.loan_detail.disbursed_at} />
              <DR label="Maturity Date" value={fast.loan_detail.maturity_date} />
              <DR label="Status" value={fast.loan_detail.status} />
              <DR label="Branch" value={fast.loan_detail.branch} />
              <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 10 }}>Fetched from Collateral Registry at {fast.fetched_at ? new Date(fast.fetched_at).toLocaleString() : "—"}</p>
            </> : <p style={{ color: "var(--muted)", fontSize: 14 }}>No loan data from Collateral Registry available for this case.</p>
          )}

          {/* Borrower */}
          {activeTab === "borrower" && (
            fast?.borrower_info ? <>
              <DR label="Full Name" value={fast.borrower_info.full_name} />
              <DR label="National ID" value={fast.borrower_info.national_id} />
              <DR label="Phone" value={fast.borrower_info.phone} />
              <DR label="Account Number" value={fast.borrower_info.account_number} />
              <DR label="Address" value={fast.borrower_info.address} />
            </> : <p style={{ color: "var(--muted)", fontSize: 14 }}>No borrower data from Collateral Registry available.</p>
          )}

          {/* Asset */}
          {activeTab === "asset" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#f59e0b" }}>Collateral Registry Record</p>
                {fast?.asset_info ? <>
                  <DR label="Type" value={fast.asset_info.asset_type} />
                  <DR label="Category" value={fast.asset_info.asset_category} />
                  <DR label="Description" value={fast.asset_info.description} />
                  <DR label="Serial / Plate" value={fast.asset_info.serial_number_or_plate} />
                  <DR label="Registered" value={fast.asset_info.registration_date} />
                  <DR label="Orig. Value" value={fast.asset_info.estimated_value_at_origination ? `ETB ${fast.asset_info.estimated_value_at_origination?.toLocaleString()}` : null} />
                  <DR label="Zone" value={fast.asset_info.location_zone} />
                </> : <p style={{ color: "var(--muted)", fontSize: 13 }}>Not available</p>}
              </div>
              <div style={{ background: "var(--bg)", border: "1px dashed var(--border)", borderRadius: "var(--radius-sm)", padding: 14 }}>
                <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>Field catalog (DAA)</p>
                {catalogPayload || catalogingPhotoUrls.length > 0 ? (
                  <>
                    {catalogPayload ? (
                      <>
                        {Boolean(catalogPayload["asset_name"]) && <DR label="Asset name" value={String(catalogPayload["asset_name"])} />}
                        {Boolean(catalogPayload["asset_category"]) && <DR label="Category" value={String(catalogPayload["asset_category"])} />}
                        {Boolean(catalogPayload["city"]) && <DR label="City / location" value={String(catalogPayload["city"])} />}
                        {Boolean(catalogPayload["condition_summary"]) && (
                          <DR label="Condition" value={String(catalogPayload["condition_summary"])} />
                        )}
                        {Boolean(catalogPayload["specifications"]) && (
                          <DR label="Specifications" value={String(catalogPayload["specifications"])} />
                        )}
                        {(Boolean(catalogPayload["asset_description"]) || Boolean(catalogPayload["description"])) && (
                          <DR
                            label="Description"
                            value={String(catalogPayload["asset_description"] ?? catalogPayload["description"] ?? "")}
                          />
                        )}
                        {catalogPayload["cataloged_at"] ? (
                          <DR
                            label="Cataloged"
                            value={new Date(String(catalogPayload["cataloged_at"])).toLocaleString()}
                          />
                        ) : null}
                      </>
                    ) : null}
                    <FieldPhotoGrid urls={catalogingPhotoUrls} />
                  </>
                ) : (
                  <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6, margin: "0 0 12px" }}>
                    Catalog photos and descriptions from the Field Seller DAA will appear here after the cataloging step is
                    completed.
                  </p>
                )}

                <p style={{ margin: "14px 0 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>Other field data</p>
                {c.module_payloads && Object.keys(c.module_payloads).length > 0 ? (
                  <>
                    {Object.entries(c.module_payloads).map(([stepId, payload]) => {
                    const p = payload as Record<string, unknown>;
                    if (!nonCatalogFieldRowVisible(stepId, p)) return null;
                    const extraImgUrls = normalizeCatalogingImageUrls(
                      Array.isArray(p["image_urls"]) ? (p["image_urls"] as string[]) : []
                    );
                    const hasPhotoList = Array.isArray(p["photos"]) && (p["photos"] as unknown[]).length > 0;
                    return (
                      <div key={stepId} style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: "var(--brand)", margin: "0 0 6px", textTransform: "uppercase" }}>{stepId.replace(/_/g, " ")}</p>
                        {Boolean(p["physical_condition"]) && <DR label="Condition" value={String(p["physical_condition"])} />}
                        {Boolean(p["valuation_estimate"]) && <DR label="Valuation Est." value={`ETB ${Number(p["valuation_estimate"]).toLocaleString()}`} />}
                        {Boolean(p["condition_grade"]) && <DR label="Grade" value={String(p["condition_grade"])} />}
                        {Boolean(p["market_value"]) && <DR label="Market Value" value={`ETB ${Number(p["market_value"]).toLocaleString()}`} />}

                        {hasPhotoList && (
                          <p style={{ fontSize: 12, color: "var(--muted)", margin: "4px 0 0" }}>{(p["photos"] as unknown[]).length} photo(s) uploaded</p>
                        )}
                        {extraImgUrls.length > 0 ? <FieldPhotoGrid urls={extraImgUrls} /> : null}
                        <p style={{ fontSize: 10, color: "var(--muted)", margin: "4px 0 0" }}>Source: {String(p["_submitted_by"] ?? "DAA")} · {p["_submitted_at"] ? new Date(String(p["_submitted_at"])).toLocaleDateString() : ""}</p>
                      </div>
                    );
                  })}
                    {Object.entries(c.module_payloads).every(([stepId, payload]) => {
                      const p = payload as Record<string, unknown>;
                      return isCatalogModuleRow(stepId, p) || !nonCatalogFieldRowVisible(stepId, p);
                    }) ? (
                      <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                        No verification, valuation, or hub inspection rows yet.
                      </p>
                    ) : null}
                  </>
                ) : null}
                {!c.module_payloads || Object.keys(c.module_payloads).length === 0 ? (
                  <p style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.6 }}>Physical condition, valuation, and other field modules will appear here as agents complete their work.</p>
                ) : null}
              </div>
            </div>
          )}

          {/* Auction Info */}
          {activeTab === "auction" && (
            editingAuction ? (
              <AuctionInfoEditor
                caseId={caseId}
                initialData={auctionInfo}
                onSaved={() => { setEditingAuction(false); reload(); }}
                onCancel={() => setEditingAuction(false)}
              />
            ) : (
              <div>
                {auctionInfo ? <>
                  <DR label="Initial Price" value={auctionInfo.initial_price ? `${auctionInfo.currency} ${auctionInfo.initial_price.toLocaleString()}` : null} />
                  <DR label="Start Time" value={auctionInfo.start_time ? new Date(auctionInfo.start_time).toLocaleString() : null} />
                  <DR label="End Time" value={auctionInfo.end_time ? new Date(auctionInfo.end_time).toLocaleString() : null} />
                  <DR label="Bid Security" value={auctionInfo.bid_security_type} />
                  <DR label="Min. Bid Amount" value={auctionInfo.min_amount_to_bid ? `${auctionInfo.currency} ${auctionInfo.min_amount_to_bid.toLocaleString()}` : null} />
                  <DR label="CPO Amount" value={auctionInfo.cpo_amount != null ? String(auctionInfo.cpo_amount) : null} />
                  <DR label="Min Increment" value={auctionInfo.min_increment ? `${auctionInfo.currency} ${auctionInfo.min_increment.toLocaleString()}` : null} />
                  <DR label="Max Increment" value={auctionInfo.max_increment ? `${auctionInfo.currency} ${auctionInfo.max_increment.toLocaleString()}` : null} />
                  <DR label="Bidding Type" value={auctionInfo.is_closed ? "Sealed (Closed)" : "Open / Live"} />
                </> : <p style={{ color: "var(--muted)", fontSize: 14 }}>No auction parameters set yet. These are configured at case initiation or can be updated before listing.</p>}
                
                {!auctionPostedToLegacy && !c.is_closed ? (
                  <div style={{ marginTop: 20, textAlign: "right" }}>
                    <button onClick={() => setEditingAuction(true)}
                      style={{ padding: "8px 16px", background: "var(--brand)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                      Edit Auction Info
                    </button>
                  </div>
                ) : auctionPostedToLegacy ? (
                  <p style={{ marginTop: 12, fontSize: 12, color: "var(--good)", fontWeight: 600 }}>
                    ✓ Auction published — parameters are locked.
                  </p>
                ) : null}

                {bidRowsSorted.length > 0 ? (
                  <div style={{ marginTop: 24 }}>
                    <p style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                      Auction result — bid history (highest first; legacy marketplace)
                    </p>
                    {(winnerFullname ?? winnerUsername) && winningBidAmount != null ? (
                      <p style={{ fontSize: 14, color: "var(--ink)", fontWeight: 700, margin: "0 0 12px" }}>
                        Winner: {winnerFullname ?? winnerUsername}
                            {" · Hammer "}
                        ETB {winningBidAmount.toLocaleString()}
                      </p>
                    ) : null}
                    <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--panel)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", color: "var(--muted)" }}>
                            <th style={{ padding: "10px 12px" }}>Bidder</th>
                            <th style={{ padding: "10px 12px" }}>Amount (ETB)</th>
                            <th style={{ padding: "10px 12px" }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bidRowsSorted.map((row, i) => {
                            const isWinner = i === 0 && (row.amount ?? 0) > 0;
                            return (
                            <tr
                              key={`${row.bid_id ?? i}-${row.user_id ?? ""}`}
                              style={{
                                borderBottom: "1px solid var(--border)",
                                background: isWinner ? "var(--good-bg, #e9f8ee)" : undefined,
                              }}
                            >
                              <td style={{ padding: "10px 12px", color: "var(--ink)" }}>
                                {row.fullname || row.username || `User #${row.user_id ?? "—"}`}
                                {isWinner ? (
                                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: "var(--good)" }}>
                                    WINNER
                                  </span>
                                ) : null}
                              </td>
                              <td style={{ padding: "10px 12px", fontWeight: 600 }}>{row.amount != null ? row.amount.toLocaleString() : "—"}</td>
                              <td style={{ padding: "10px 12px", color: "var(--muted)" }}>{row.status ?? "—"}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 }}>
                      Synced from the auction platform by AEOS. Use Winner Approval below to record formal lender consent.
                    </p>
                  </div>
                ) : auctionPostedToLegacy && (mktFlags["bidding_started"] === true) ? (
                  <p style={{ marginTop: 20, fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
                    No bid rows on file yet. After the auction closes, results appear here automatically from the marketplace.
                  </p>
                ) : null}
              </div>
            )
          )}

          {/* Memo */}
          {activeTab === "memo" && (
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {memos.length === 0 && <p style={{ color: "var(--muted)", fontSize: 14 }}>No memos recorded yet.</p>}
                {memos.map((m, i) => (
                  <div key={i} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
                    <p style={{ margin: 0, fontSize: 14, color: "var(--ink)", lineHeight: 1.6 }}>{m.text}</p>
                    <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--muted)" }}>{m.author_id} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}</p>
                  </div>
                ))}
              </div>
              <label htmlFor="new-memo" style={{ ...lbl, fontSize: 12 }}>Add Memo</label>
              <textarea id="new-memo" rows={3} value={memoText} onChange={(e) => setMemoText(e.target.value)}
                placeholder="Add an internal note or observation…"
                style={{ ...fieldStyle, resize: "vertical", marginBottom: 10 }} />
              <button onClick={() => void submitMemo()} disabled={savingMemo || !memoText.trim()}
                style={{ padding: "10px 20px", borderRadius: "var(--radius-sm)", border: "none", background: !memoText.trim() ? "var(--muted)" : "var(--brand)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: !memoText.trim() ? "not-allowed" : "pointer" }}>
                {savingMemo ? "Saving…" : "Add Memo"}
              </button>
            </div>
          )}

          {/* History & Audit Trail */}
          {activeTab === "history" && <CaseHistoryTab caseId={caseId} />}

          {/* Complaints */}
          {activeTab === "complaints" && <CaseComplaintsTab caseId={caseId} />}
        </div>
      </div>

      {/* Institution workflow modules */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <ModuleShell title="Case Initiation" description="Institution initiates the case from Collateral Registry data."
          startFlag={inst["initiation_started"] ?? null} completeFlag={inst["initiation_completed"] ?? null} theme="governance"
          activeContent={<p style={{ color: "var(--muted)", fontSize: 14, margin: 0 }}>Initiation module is active.</p>}
          completedContent={<p style={{ fontSize: 14, color: "var(--ink)" }}>Case initiated. Loan: {c.linked_loan_id} · Borrower: {c.borrower_id}</p>} />

        <ModuleShell title="Auction Approval" description="Set auction parameters and approve the catalog for platform listing."
          startFlag={inst["auction_approval_started"] ?? null} completeFlag={inst["auction_approval_completed"] ?? null} theme="governance"
          activeContent={
            <AuctionApprovalForm
              caseId={caseId}
              initialAuctionInfo={auctionInfo}
              catalogingPhotoUrls={catalogingPhotoUrls}
              onApproved={reload}
            />
          }
          completedContent={<p style={{ fontSize: 14, color: "var(--ink)" }}>Auction approved. Listing submitted to platform.</p>} />

        <ModuleShell title="Bidding Approval" description="Formally authorize the platform to open live bidding."
          startFlag={inst["approved_bidding_started"] ?? null} completeFlag={inst["approved_bidding_completed"] ?? null} theme="governance"
          activeContent={
            <ApprovedBiddingForm
              caseId={caseId}
              platformAuctionId={c.platform_auction_id}
              onApproved={reload}
            />
          }
          completedContent={<p style={{ fontSize: 14, color: "var(--ink)" }}>Bidding authorization recorded. Auction in progress.</p>} />

        <ModuleShell title="Winner Approval" description="Review and approve the auction winner."
          startFlag={inst["winner_approval_started"] ?? null} completeFlag={inst["winner_approval_completed"] ?? null} theme="governance"
          activeContent={
            <WinnerApprovalForm
              caseId={caseId}
              winnerId={c.winner_participant_id}
              winningBidAmount={winningBidAmount}
              winnerFullname={winnerFullname}
              winnerUsername={winnerUsername}
              bidRows={bidRowsSorted}
              platformAuctionId={c.platform_auction_id}
              onApproved={reload}
            />
          }
          completedContent={<p style={{ fontSize: 14, color: "var(--ink)" }}>Winner approved: {c.winner_participant_id ?? "—"}</p>} />

        <ModuleShell title="Settlement" description="Complete the final settlement and close the case."
          startFlag={inst["settlement_started"] ?? null} completeFlag={inst["settlement_completed"] ?? null} theme="governance"
          activeContent={
            <SettlementForm
              caseId={caseId}
              winnerParticipantId={c.winner_participant_id}
              winningBidAmount={winningBidAmount}
              onSettled={reload}
            />
          }
          completedContent={<p style={{ fontSize: 14, color: "var(--good)", fontWeight: 700 }}>Settlement complete. Case closed.</p>} />
      </div>
    </>
  );
}
